import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const API_BASE = API_CONFIG.API_BASE;

  // Check for existing session on app load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API_BASE}/user/profile`);
          
          setUser(response.data);
          setIsAuthenticated(true);
          console.log('✅ Existing session restored:', response.data);
        } catch (error) {
          console.error('❌ Session validation failed:', error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setIsAuthenticated(false);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (username, password) => {
    try {
      console.log('🔐 Attempting login for:', username);
      
      const response = await axios.post(`${API_BASE}/auth/login`, {
        username,
        password
      });
      
      console.log('✅ Login API response:', response.data);
      
      if (response.data.success && response.data.token) {
        const { token } = response.data;
        
        // Store token
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        console.log('🔑 Token stored, fetching user profile...');
        
        // Fetch user profile
        try {
          const profileResponse = await axios.get(`${API_BASE}/user/profile`);
          console.log('👤 Profile fetched:', profileResponse.data);
          
          setUser(profileResponse.data);
          setIsAuthenticated(true);
          
          console.log('🎯 Redirecting to dashboard...');
          
          // Force navigation to dashboard
          navigate('/dashboard', { replace: true });
          
          return { success: true };
        } catch (profileError) {
          console.error('❌ Profile fetch failed:', profileError);
          throw profileError;
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('❌ Login failed:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || error.message || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      console.log('📝 Attempting registration for:', userData.username);
      
      // Note: This should only be called after email verification
      // The registration UI should handle OTP verification first
      const response = await axios.post(`${API_BASE}/auth/register`, userData);

      console.log('✅ Registration successful');
      return {
        success: true,
        message: response.data.message || 'Registration successful!',
        pending_approval: response.data.pending_approval ?? false,
      };
    } catch (error) {
      console.error('❌ Registration failed:', error);
      
      // Check if the error is due to unverified email
      if (error.response?.status === 400 && 
          error.response?.data?.detail?.includes('Email must be verified')) {
        return { 
          success: false, 
          message: 'Email verification required. Please verify your email first.',
          requiresEmailVerification: true
        };
      }
      
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    console.log('👋 Logging out user');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login', { replace: true });
  };

  const value = {
    isAuthenticated,
    user,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
